import React, { useEffect, useMemo, useState } from 'react'
import { Input, Popup, Toast } from 'antd-mobile'
import { CheckOutline } from 'antd-mobile-icons'
import axios from 'axios'
import API_ROUTES from '../../api'
import UploadImgToCND from '../../Auth/ChangeUser/upload'
import Icon from '../../icon'

const DEFAULT_OPPONENT_AVATAR = 'https://huaxi-kepeng.oss-cn-chengdu.aliyuncs.com/kplogo.png'

// 正式赛、活动赛共用的对手选择面板：既可复用已有外战队伍，也可继续手动新增。
export default function OpponentPickerPopup({ visible, onClose, gameDetail, memberList, onSaved }) {
    const [mode, setMode] = useState('existing')
    const [selectedMemberId, setSelectedMemberId] = useState('')
    const [opponentName, setOpponentName] = useState('')
    const [opponentImage, setOpponentImage] = useState('')
    const [submitting, setSubmitting] = useState(false)

    const opponentMembers = useMemo(() => (
        (memberList || []).filter(item => Number(item?.tempMember ?? item?.temp_member) === 2)
    ), [memberList])
    const currentOpponentId = gameDetail?.redCaptain?.id || gameDetail?.redCaptainId || ''
    const currentOpponentName = gameDetail?.redCaptain?.name || gameDetail?.redName || ''
    const currentOpponentImage = gameDetail?.redCaptain?.image
        || gameDetail?.redCaptainImage
        || gameDetail?.redTeamLogo
        || ''

    useEffect(() => {
        if (!visible) return
        // 已经选择过对手时默认进入编辑页，同时回显当前队伍供重新切换。
        setMode(currentOpponentId ? 'edit' : (opponentMembers.length > 0 ? 'existing' : 'new'))
        setSelectedMemberId(currentOpponentId)
        setOpponentName(currentOpponentName)
        setOpponentImage('')
    }, [visible, opponentMembers.length, currentOpponentId, currentOpponentName])

    const getOpponentAvatar = (member) => {
        const image = member?.memberImageList?.find(item => Number(item.type) === 0)?.image
        if (!image) return DEFAULT_OPPONENT_AVATAR
        return image.startsWith('http') ? `${image}${Icon.ImageFooter}` : DEFAULT_OPPONENT_AVATAR
    }

    const handleClose = () => {
        if (!submitting) onClose?.()
    }

    const handleSubmit = async () => {
        if (mode === 'existing' && !selectedMemberId) {
            Toast.show({ icon: 'fail', content: '请选择一个对手队伍' })
            return
        }
        if ((mode === 'new' || mode === 'edit') && !opponentName.trim()) {
            Toast.show({ icon: 'fail', content: '请输入对手名称' })
            return
        }

        const formData = new FormData()
        formData.append('gameId', gameDetail?.id)
        if (mode === 'existing') {
            formData.append('memberId', selectedMemberId)
        } else if (mode === 'edit') {
            formData.append('memberId', currentOpponentId)
            formData.append('name', opponentName.trim())
        } else {
            formData.append('name', opponentName.trim())
            formData.append('image', opponentImage || '')
        }

        setSubmitting(true)
        Toast.show({ icon: 'loading', content: '正在添加对手...', duration: 0 })
        try {
            const response = await axios.post(API_ROUTES.game_outGameInfo, formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            })
            Toast.clear()
            if (response.data.code !== 200) {
                Toast.show({ icon: 'fail', content: response.data.msg || '添加失败，请稍后重试' })
                return
            }
            Toast.show({ icon: 'success', content: '对手添加成功' })
            onClose?.()
            onSaved?.()
        } catch (error) {
            Toast.clear()
            Toast.show({ icon: 'fail', content: '添加失败，请联系管理员' })
        } finally {
            setSubmitting(false)
        }
    }

    return (
        <Popup
            visible={visible}
            position='bottom'
            onMaskClick={handleClose}
            bodyClassName='opponent-picker-popup'
            destroyOnClose
        >
            <div className='opponent-picker-header'>
                <button type='button' onClick={handleClose} disabled={submitting}>取消</button>
                <div>
                    <strong>{currentOpponentId ? '管理对手' : '添加对手'}</strong>
                    <span>{currentOpponentId ? '编辑当前信息或切换其他队伍' : '选择已有队伍或录入新对手'}</span>
                </div>
                <button type='button' className='is-confirm' onClick={handleSubmit} disabled={submitting}>
                    {submitting ? '处理中' : '确认'}
                </button>
            </div>

            <div className='opponent-picker-content'>
                <div className={`opponent-mode-tabs ${currentOpponentId ? 'has-current' : ''}`}>
                    <button
                        type='button'
                        className={mode === 'existing' ? 'is-active' : ''}
                        onClick={() => setMode('existing')}
                    >
                        选择已有队伍
                    </button>
                    {currentOpponentId && (
                        <button
                            type='button'
                            className={mode === 'edit' ? 'is-active' : ''}
                            onClick={() => {
                                setMode('edit')
                                setOpponentName(currentOpponentName)
                            }}
                        >
                            编辑当前
                        </button>
                    )}
                    <button
                        type='button'
                        className={mode === 'new' ? 'is-active' : ''}
                        onClick={() => setMode('new')}
                    >
                        新增对手
                    </button>
                </div>

                {mode === 'existing' ? (
                    <section className='opponent-picker-section'>
                        <div className='opponent-picker-section-title'>
                            <strong>外战队伍</strong>
                            <span>共 {opponentMembers.length} 个可选</span>
                        </div>
                        {opponentMembers.length > 0 ? (
                            <div className='opponent-member-list'>
                                {opponentMembers.map(member => {
                                    const selected = selectedMemberId === member.id
                                    return (
                                        <button
                                            type='button'
                                            key={member.id}
                                            className={`opponent-member-card ${selected ? 'is-selected' : ''}`}
                                            onClick={() => setSelectedMemberId(selected ? '' : member.id)}
                                        >
                                            <img src={getOpponentAvatar(member)} alt={member.name || '对手队伍'} />
                                            <span>
                                                <strong>{member.name || '未命名队伍'}</strong>
                                                <small>外战队伍 · 单人代表</small>
                                            </span>
                                            {selected && <i><CheckOutline /></i>}
                                        </button>
                                    )
                                })}
                            </div>
                        ) : (
                            <div className='opponent-empty-state'>
                                <span>⚽</span>
                                <strong>暂无已有外战队伍</strong>
                                <p>可以切换到“新增对手”继续录入。</p>
                            </div>
                        )}
                    </section>
                ) : mode === 'edit' ? (
                    <section className='opponent-picker-section opponent-create-form'>
                        <div className='opponent-current-tip'>
                            <img src={currentOpponentImage || DEFAULT_OPPONENT_AVATAR} alt={currentOpponentName || '当前对手'} />
                            <span><strong>{currentOpponentName || '当前对手'}</strong><small>正在编辑当前比赛对手</small></span>
                        </div>
                        <label>
                            <span>对手名称</span>
                            <Input
                                value={opponentName}
                                maxLength={20}
                                clearable
                                placeholder='请输入对手名称'
                                onChange={setOpponentName}
                            />
                        </label>
                        <div className='opponent-upload-field'>
                            <span>对手头像</span>
                            <div>
                                <UploadImgToCND
                                    memberInfo={currentOpponentId}
                                    type={0}
                                    adminDirect
                                    value={currentOpponentImage ? [{ url: currentOpponentImage }] : []}
                                    onChange={setOpponentImage}
                                />
                                <p>重新上传后头像会直接替换；点击右上角确认保存名称。</p>
                            </div>
                        </div>
                    </section>
                ) : (
                    <section className='opponent-picker-section opponent-create-form'>
                        <label>
                            <span>对手名称</span>
                            <Input
                                value={opponentName}
                                maxLength={20}
                                clearable
                                placeholder='请输入对手名称'
                                onChange={setOpponentName}
                            />
                        </label>
                        <div className='opponent-upload-field'>
                            <span>对手头像</span>
                            <div>
                                <UploadImgToCND oss={1} onChange={setOpponentImage} />
                                <p>建议上传正方形队徽或头像，未上传时使用默认队徽。</p>
                            </div>
                        </div>
                    </section>
                )}
            </div>
        </Popup>
    )
}
